import { Menu, X } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { NAV } from "@/constants/testIds";

// Anchor links jump to on-page sections (only meaningful from the home route,
// but they still work from any page because React Router preserves the hash
// and browsers auto-scroll to the matching id after route resolution).
const anchorLinks = [
  { href: "/#parlay-calculator", label: "Parlay Calculator", testId: NAV.linkParlay },
  { href: "/#odds-converter", label: "Odds Converter", testId: NAV.linkConverter },
  { href: "/#how-it-works", label: "How It Works", testId: NAV.linkHow },
];

const routeLinks = [
  { to: "/about", label: "About", testId: NAV.linkAbout },
  { to: "/playbook", label: "Playbook", testId: NAV.linkPlaybook },
  { to: "/round-robin-calculator", label: "Round Robin", testId: NAV.linkRoundRobin },
  { to: "/player-props", label: "Prop Odds", testId: NAV.linkPropOdds },
  { to: "/analyzer", label: "Analyzer", testId: NAV.linkAnalyzer },
  { to: "/sponsors", label: "Sponsors", testId: NAV.linkSponsors },
];

export const Header = ({ variant = "home" }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = variant === "home" && location.pathname === "/";

  return (
    <header className="w-full">
      <div className="max-w-6xl mx-auto px-5 md:px-8 pt-6 md:pt-10">
        <div className="flex items-center justify-between gap-6">
          <Link
            to="/"
            data-testid={NAV.brand}
            className="group inline-flex items-center gap-3"
          >
            <span
              aria-hidden="true"
              className="inline-flex items-center justify-center w-10 h-10 rounded-xl"
              style={{
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                boxShadow: "0 8px 24px -8px rgba(16,185,129,0.6)",
              }}
            >
              <span className="font-display text-white text-xl leading-none">PA</span>
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-display text-white text-xl md:text-2xl tracking-wide">
                Parlay Assistant
              </span>
              <span className="text-xs md:text-sm text-slate-400 hidden sm:block">
                Calculate parlay payouts and convert betting odds instantly.
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-7" aria-label="Primary">
            {anchorLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={l.testId}
                className="pa-nav-link font-display uppercase tracking-wide"
              >
                {l.label}
              </a>
            ))}
            {routeLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className="pa-nav-link font-display uppercase tracking-wide"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-menu-toggle"
            className="lg:hidden pa-btn pa-btn-dark-ghost !px-3 !py-2"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden mt-4 pa-card-dark p-4 flex flex-col gap-3">
            {anchorLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={l.testId + "-mobile"}
                className="pa-nav-link font-display uppercase tracking-wide"
                onClick={() => setOpen(false)}
              >
                {l.label}
              </a>
            ))}
            {routeLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testId + "-mobile"}
                className="pa-nav-link font-display uppercase tracking-wide"
                onClick={() => setOpen(false)}
              >
                {l.label}
              </Link>
            ))}
          </div>
        )}
      </div>

      {isHome && (
        <div className="max-w-6xl mx-auto px-5 md:px-8 mt-10 md:mt-16">
          <div className="max-w-3xl">
            <span
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs tracking-wider uppercase"
              style={{
                background: "rgba(16,185,129,0.12)",
                color: "#6ee7b7",
                border: "1px solid rgba(16,185,129,0.35)",
                fontFamily: "IBM Plex Mono, monospace",
              }}
            >
              <span
                aria-hidden="true"
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: "#10b981" }}
              />
              Free · No signup · Runs in your browser
            </span>
            <h1 className="mt-5 font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight uppercase">
              Free Parlay Calculator
              <br />
              <span style={{ color: "#10b981" }}>&amp;</span> Betting Odds Converter
            </h1>
            <p className="mt-5 text-slate-300 max-w-2xl text-base md:text-lg">
              Multiple clean tools, one page. Estimate parlay payouts across up to fifteen legs, or
              flip between American, decimal, and fractional odds in a single tap.
            </p>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
